import React, { useState, useCallback, useRef } from 'react';
import { FaTwitter, FaYoutube, FaInstagram, FaTiktok, FaFacebookF } from 'react-icons/fa';
import AdSlot from './AdSlot';
import './App.css';

const PLATFORMS = [
  { key: 'Twitter',   icon: FaTwitter,   color: '#1DA1F2', patterns: ['twitter.com', 'x.com'] },
  { key: 'Youtube',   icon: FaYoutube,   color: '#FF0000', patterns: ['youtube.com', 'youtu.be'] },
  { key: 'Instagram', icon: FaInstagram, color: '#E4405F', patterns: ['instagram.com'] },
  { key: 'TikTok',    icon: FaTiktok,    color: '#00F2EA', patterns: ['tiktok.com'] },
  { key: 'Facebook',  icon: FaFacebookF, color: '#1877F2', patterns: ['facebook.com', 'fb.watch'] },
];

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

function detectPlatform(url) {
  const lower = url.toLowerCase();
  return PLATFORMS.find(p => p.patterns.some(pat => lower.includes(pat))) || null;
}

function App() {
  const [platform, setPlatform] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formats, setFormats] = useState([]);
  const [error, setError] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  const [downloading, setDownloading] = useState(null);
  const [dlProgress, setDlProgress] = useState(0);
  const [dlStatus, setDlStatus] = useState('');
  const [dlInfo, setDlInfo] = useState('');
  const [dlFileName, setDlFileName] = useState('');
  const abortRef = useRef(null);

  // Auto-detect platform when URL changes
  const handleUrlChange = useCallback((value) => {
    setUrl(value);
    const detected = detectPlatform(value);
    if (detected) {
      setPlatform(detected.key);
      setError('');
    }
  }, []);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      handleUrlChange(text);
    } catch {
      setError('Panoya erişim izni verilmedi.');
    }
  }, [handleUrlChange]);

  const handleFetchFormats = useCallback(async () => {
    if (!url.trim()) {
      setError('Lütfen bir URL girin.');
      return;
    }

    const detected = detectPlatform(url.trim());
    if (!detected) {
      setError('Bu URL desteklenen platformlara ait değil. (Twitter, YouTube, Instagram, TikTok)');
      return;
    }

    setPlatform(detected.key);
    setError('');
    setFormats([]);
    setLoading(true);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress(prev => prev >= 90 ? (clearInterval(interval), 90) : prev + 10);
    }, 200);

    try {
      const response = await fetch(`${API}/api/video-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), platform: detected.key.toLowerCase() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Video bilgisi alınamadı.');
      }

      const data = await response.json();
      setVideoTitle(data.title || '');
      setFormats(data.formats || []);
      setProgress(100);
    } catch (err) {
      setError(
        err.message === 'Failed to fetch'
          ? 'Backend sunucusu çalışmıyor. Lütfen "node server.js" ile başlatın.'
          : err.message
      );
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  }, [url]);

  const handleFormatDownload = useCallback((format) => {
    if (downloading) return;

    const detected = detectPlatform(url.trim());
    const params = new URLSearchParams({
      url: url.trim(),
      platform: (detected?.key || 'youtube').toLowerCase(),
      formatId: format.formatId,
      ext: format.ext,
    });

    setDownloading(format.formatId);
    setDlProgress(0);
    setDlStatus('Hazırlanıyor...');
    setDlInfo('');
    setDlFileName('');
    setError('');

    const evtSource = new EventSource(`${API}/api/prepare?${params.toString()}`);
    abortRef.current = evtSource;

    evtSource.onmessage = (e) => {
      const data = JSON.parse(e.data);

      switch (data.type) {
        case 'status':
          setDlStatus(data.message);
          if (data.fileName) setDlFileName(data.fileName);
          break;

        case 'progress':
          setDlProgress(Math.round(data.percent));
          const parts = [];
          if (data.size) parts.push(data.size);
          if (data.speed) parts.push(data.speed);
          if (data.eta) parts.push(`ETA ${data.eta}`);
          setDlInfo(parts.join(' · '));
          setDlStatus('İndiriliyor...');
          break;

        case 'done':
          setDlProgress(100);
          setDlStatus('Tamamlandı! Dosya kaydediliyor...');
          evtSource.close();

          const a = document.createElement('a');
          a.href = `${API}/api/file/${data.fileId}`;
          a.download = data.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          setTimeout(() => {
            setDownloading(null);
            setDlProgress(0);
            setDlStatus('');
            setDlInfo('');
          }, 2000);
          break;

        case 'error':
          evtSource.close();
          setError(data.message);
          setDownloading(null);
          break;

        default:
          break;
      }
    };

    evtSource.onerror = () => {
      evtSource.close();
      if (downloading) {
        setError('Bağlantı kesildi.');
        setDownloading(null);
      }
    };
  }, [url, downloading]);

  const handleCancelDownload = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.close();
      abortRef.current = null;
    }
    setDownloading(null);
    setDlProgress(0);
    setDlStatus('');
    setDlInfo('');
  }, []);

  return (
    <div className="app">
      {/* Üst Reklam — Banner */}
      <AdSlot slot="XXXXXXXXXX" format="horizontal" className="ad-top" />

      <div className="container">
        <h1 className="title">Video Downloader</h1>

        {/* Platform Indicators */}
        <div className="platform-toggle">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const isActive = platform === p.key;
            return (
              <button
                key={p.key}
                className={`platform-btn ${isActive ? 'active' : ''}`}
                style={isActive ? { '--platform-color': p.color } : {}}
                onClick={() => { setPlatform(p.key); setFormats([]); setError(''); setVideoTitle(''); }}
              >
                <Icon className="platform-icon" />
                <span>{p.key}</span>
              </button>
            );
          })}
        </div>

        {/* URL Input */}
        <div className="url-row">
          <input
            type="text"
            className="url-input"
            placeholder="Video URL yapıştırın..."
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFetchFormats()}
          />
          <button className="paste-btn" onClick={handlePaste}>
            Paste
          </button>
        </div>

        {/* Download Button */}
        <button
          className="download-btn"
          onClick={handleFetchFormats}
          disabled={loading || !!downloading}
        >
          {loading ? 'Yükleniyor...' : 'Download'}
        </button>

        {/* Fetch Progress Bar */}
        {loading && (
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}>
                <div className="progress-glow" />
              </div>
            </div>
            <span className="progress-text">LOADING.....</span>
          </div>
        )}

        {/* Error */}
        {error && <div className="error-msg">{error}</div>}

        {/* Video Title */}
        {videoTitle && <div className="video-title">{videoTitle}</div>}

        {/* Download Progress */}
        {downloading && (
          <div className="dl-progress-container">
            <div className="dl-progress-header">
              <div className="dl-progress-left">
                <span className="dl-progress-label">{dlStatus}</span>
                {dlFileName && <span className="dl-file-name">{dlFileName}</span>}
                {dlInfo && <span className="dl-info">{dlInfo}</span>}
              </div>
              <button className="dl-cancel-btn" onClick={handleCancelDownload}>
                İptal
              </button>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill dl-fill"
                style={{ width: `${dlProgress}%` }}
              >
                {dlProgress > 0 && <div className="progress-glow" />}
              </div>
            </div>
            <span className="dl-percent">{dlProgress > 0 ? `%${dlProgress}` : ''}</span>
          </div>
        )}

        {/* Format Buttons */}
        {formats.length > 0 && (
          <div className="formats-grid">
            {formats.map((f, i) => (
              <button
                key={i}
                className={`format-btn ${downloading === f.formatId ? 'downloading' : ''}`}
                onClick={() => handleFormatDownload(f)}
                disabled={!!downloading}
              >
                <span className="format-ext">{f.ext.toUpperCase()}</span>
                {f.quality && (
                  <span className="format-quality">
                    {f.quality} {f.label && <sup className="format-label">{f.label}</sup>}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Orta Reklam — Formatların altı */}
        {formats.length > 0 && (
          <AdSlot slot="XXXXXXXXXX" format="rectangle" className="ad-middle" />
        )}
      </div>

      {/* Alt Reklam — Banner */}
      <AdSlot slot="XXXXXXXXXX" format="horizontal" className="ad-bottom" />

      {/* Footer */}
      <footer className="footer">
        <span>&copy; {new Date().getFullYear()} hdvideodownload.com — Tüm hakları saklıdır.</span>
        <a href="mailto:hdvideodownload44@gmail.com" className="footer-mail">hdvideodownload44@gmail.com</a>
      </footer>
    </div>
  );
}

export default App;
